import { useEffect, useState } from 'react';
import {
	Box,
	DialogContent,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	Stack,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import {
	CheckCircleRounded,
	DescriptionOutlined,
	ImageOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from './MoleDialogShell';
import { Z_INDEX } from '../styles/zIndex';
import LineImagesReview from './docImages/LineImagesReview';
import { DocImageKind } from './docImages/docImagesApi';

export type DocumentExportLayout = 'plain' | 'images';
export type DocumentImageSize = 'xs' | 's' | 'm' | 'l' | 'xl' | '2xl';

const IMAGE_SIZES: DocumentImageSize[] = ['xs', 's', 'm', 'l', 'xl', '2xl'];
const IMAGE_SIZE_CUE_PX: Record<DocumentImageSize, number> = {
	xs: 8,
	s: 10,
	m: 12,
	l: 14,
	xl: 16,
	'2xl': 20,
};

interface Props {
	open: boolean;
	onClose: () => void;
	onExport: (layout: DocumentExportLayout, imageSize: DocumentImageSize) => void;
	/** The document being exported; with it, "With images" first reviews each line's image. */
	document?: { kind: DocImageKind; batch: string };
	/** Whether image choices may be changed and persisted on this document. */
	canEditImages?: boolean;
}

const DocumentExportDialog = ({
	open,
	onClose,
	onExport,
	document,
	canEditImages = false,
}: Props) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const themeColor = useMoleThemeColor();
	const [layout, setLayout] = useState<DocumentExportLayout>('plain');
	const [imageSize, setImageSize] = useState<DocumentImageSize>('m');
	const [reviewing, setReviewing] = useState(false);

	useEffect(() => {
		if (!open) return;
		setLayout('plain');
		setImageSize('m');
		setReviewing(false);
	}, [open]);

	const handleOpen = () => {
		if (layout === 'images' && document) {
			setReviewing(true);
			return;
		}
		onExport(layout, imageSize);
		onClose();
	};

	const optionCardSx = (selected: boolean) => ({
		flex: 1,
		p: 2.5,
		borderRadius: 2.5,
		border: '2px solid',
		borderColor: selected ? themeColor : 'divider',
		bgcolor: selected ? alpha(themeColor, 0.08) : 'background.paper',
		cursor: 'pointer',
		position: 'relative' as const,
		transition: 'all 0.2s ease',
		'&:hover': {
			borderColor: themeColor,
			bgcolor: alpha(themeColor, 0.06),
			transform: 'translateY(-2px)',
			boxShadow: theme.shadows[4],
		},
	});

	return (
		<>
		<MoleDialog open={open && !reviewing} onClose={onClose} maxWidth="sm" fullWidth themeColor={themeColor} sx={{ zIndex: Z_INDEX.nestedDialog }}>
			<MoleDialogHeader
				title={t('common:offer.export.title')}
				subtitle={t('common:offer.export.subtitle')}
				icon={<DescriptionOutlined />}
				onClose={onClose}
				themeColor={themeColor}
			/>

			<DialogContent sx={{ pt: 1 }}>
				<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, mt: 0.5 }}>
					{t('common:offer.export.chooseLayout')}
				</Typography>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
					<Box
						role="button"
						tabIndex={0}
						aria-pressed={layout === 'plain'}
						onClick={() => setLayout('plain')}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								setLayout('plain');
							}
						}}
						sx={optionCardSx(layout === 'plain')}
					>
						{layout === 'plain' && (
							<CheckCircleRounded
								sx={{ position: 'absolute', top: 10, right: 10, color: themeColor, fontSize: 22 }}
							/>
						)}
						<PlainDocumentCue accent={themeColor} />
						<Typography variant="subtitle1" fontWeight={700}>
							{t('common:offer.export.plain.title')}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{t('common:offer.export.plain.description')}
						</Typography>
					</Box>
					<Box
						role="button"
						tabIndex={0}
						aria-pressed={layout === 'images'}
						onClick={() => setLayout('images')}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								setLayout('images');
							}
						}}
						sx={optionCardSx(layout === 'images')}
					>
						{layout === 'images' && (
							<CheckCircleRounded
								sx={{ position: 'absolute', top: 10, right: 10, color: themeColor, fontSize: 22 }}
							/>
						)}
						<ImagesDocumentCue accent={themeColor} size={imageSize} />
						<Stack direction="row" spacing={0.75} alignItems="center">
							<ImageOutlined sx={{ fontSize: 20, color: themeColor }} />
							<Typography variant="subtitle1" fontWeight={700}>
								{t('common:offer.export.images.title')}
							</Typography>
						</Stack>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{t('common:offer.export.images.description')}
						</Typography>
					</Box>
				</Stack>

				{layout === 'images' && (
					<FormControl
						component="fieldset"
						sx={{
							mt: 2.5,
							px: 1.5,
							py: 1.25,
							width: '100%',
							borderRadius: 2,
							border: '1px solid',
							borderColor: alpha(themeColor, 0.2),
							bgcolor: alpha(themeColor, 0.04),
						}}
					>
						<FormLabel
							component="legend"
							sx={{ fontWeight: 700, fontSize: 13, color: 'text.primary', mb: 0.5 }}
						>
							{t('common:offer.export.imageSize.label')}
						</FormLabel>
						<RadioGroup
							row
							value={imageSize}
							onChange={(event) => setImageSize(event.target.value as DocumentImageSize)}
							sx={{ flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}
						>
							{IMAGE_SIZES.map((size) => (
								<FormControlLabel
									key={size}
									value={size}
									sx={{ mr: 1 }}
									control={<Radio size="small" sx={{ color: themeColor, '&.Mui-checked': { color: themeColor } }} />}
									label={
										<Stack direction="row" spacing={0.75} alignItems="center">
											<Box
												sx={{
													width: IMAGE_SIZE_CUE_PX[size],
													height: IMAGE_SIZE_CUE_PX[size],
													borderRadius: 0.5,
													background: `linear-gradient(135deg, ${alpha(themeColor, 0.55)} 0%, ${alpha(themeColor, 0.18)} 100%)`,
													border: '1px solid',
													borderColor: alpha(themeColor, 0.35),
													flexShrink: 0,
												}}
											/>
											<Typography variant="body2" fontWeight={700}>
												{t(`common:offer.export.imageSize.${size}`)}
											</Typography>
										</Stack>
									}
								/>
							))}
						</RadioGroup>
					</FormControl>
				)}
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={onClose}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
				<MolePrimaryButton onClick={handleOpen}>
					{t('common:ui.buttons.open')}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
		{document && (
			<LineImagesReview
				open={open && reviewing}
				kind={document.kind}
				batch={document.batch}
				canEdit={canEditImages}
				onClose={() => setReviewing(false)}
				onApproved={() => {
					onExport(layout, imageSize);
					onClose();
				}}
			/>
		)}
		</>
	);
};

const cuePaperSx = {
	width: 92,
	height: 118,
	borderRadius: 1.25,
	border: '1px solid',
	borderColor: 'divider',
	bgcolor: 'background.paper',
	p: 1,
	display: 'flex',
	flexDirection: 'column',
	gap: 0.7,
	boxShadow: 1,
	mx: 'auto',
	mb: 1.75,
	position: 'relative' as const,
};

const PlainDocumentCue = ({ accent }: { accent: string }) => (
	<Box sx={cuePaperSx} aria-hidden>
		<Box sx={{ height: 7, width: '72%', borderRadius: 1, bgcolor: alpha(accent, 0.35) }} />
		{[0, 1, 2, 3].map((i) => (
			<Box
				key={i}
				sx={{
					height: 4,
					width: i === 3 ? '58%' : '100%',
					borderRadius: 1,
					bgcolor: 'action.disabledBackground',
				}}
			/>
		))}
		<Box
			sx={{
				mt: 'auto',
				height: 10,
				borderRadius: 0.75,
				bgcolor: alpha(accent, 0.12),
				border: '1px dashed',
				borderColor: alpha(accent, 0.25),
			}}
		/>
	</Box>
);

const ImagesDocumentCue = ({
	accent,
	size,
}: {
	accent: string;
	size: DocumentImageSize;
}) => {
	const thumb = IMAGE_SIZE_CUE_PX[size];
	return (
		<Box sx={cuePaperSx} aria-hidden>
			<Box sx={{ height: 7, width: '72%', borderRadius: 1, bgcolor: alpha(accent, 0.35) }} />
			{[0, 1, 2].map((i) => (
				<Stack key={i} direction="row" spacing={0.6} alignItems="center">
					<Box
						sx={{
							width: thumb,
							height: thumb,
							borderRadius: 0.5,
							flexShrink: 0,
							background: `linear-gradient(135deg, ${alpha(accent, 0.55)} 0%, ${alpha(accent, 0.18)} 100%)`,
							border: '1px solid',
							borderColor: alpha(accent, 0.35),
						}}
					/>
					<Box
						sx={{
							flex: 1,
							height: 4,
							borderRadius: 1,
							bgcolor: 'action.disabledBackground',
						}}
					/>
				</Stack>
			))}
			<Box
				sx={{
					mt: 'auto',
					height: 10,
					borderRadius: 0.75,
					bgcolor: alpha(accent, 0.12),
					border: '1px dashed',
					borderColor: alpha(accent, 0.25),
				}}
			/>
		</Box>
	);
};

export default DocumentExportDialog;
